import React from "react"
import moment from "moment"
import styled from "styled-components"

// components
import { DropdownButton } from "quorumdesign"

// constants
import sheetConstants from "app/static/frontend/sheets/constants"
import { columnWidthConstants } from "app/static/frontend/search-new/containers/TableView/constants"
import { centsToCurrencyStr } from "shared/pac/helperFunctions"

/**
 * Get a config (Object) for a resource table column
 * to see what each config value does check out propTypes in app/static/frontend/sheets/containers/ResourceTableContainer.jsx
 * @param {Function} display optional function for column display logic
 * @param {String} enumLabel which value of an Enum you would like to display in the column
 * @param {String} field name of the field where we will find the data on the object to display in this column
 * @param {String} name value we want to display at the top of column
 * @param columnSortingQuerymethod param to pass on to the API, to sort by this field
 * @param sortOn for columns created from multiple fields, specify which one to use for sorting
 * @param {Boolean} sortable can this column be sorted
 * @param {Number} width size of the column in pixels
 * @param {'left' | 'right' | 'center' | undefined} align text alignment
 * @returns {Object} valid config for a ResourceTable column
 * NOTE: this is called a basic column config because it displays a string rather than a component in search table view
 */
export const getBasicColumnConfig = ({
    display,
    enumLabel,
    field,
    name,
    columnSortingQuerymethod,
    sortOn,
    sortable = false,
    width = columnWidthConstants.STANDARD_COLUMN_WIDTH,
    align = undefined,
}) => ({
    columnSortingQuerymethod,
    display: (...contents) => {
        if (align) {
            return <AlignedCell align={align}>{display ? display(...contents) : contents[0]}</AlignedCell>
        } else {
            return display ? display(...contents) : contents[0]
        }
    },
    enumLabel,
    field,
    name,
    resourceTableEnableSorting: !!(sortable || sortOn || columnSortingQuerymethod),
    resourceTableSortOn: sortOn,
    width,
})

/**
 * Get a config (Object) for a resource table column with a display function to display a date in a uniform way
 * @param {String} field name of the field where we will find the data on the object to display in this column
 * @param {String} name value we want to display at the top of column
 * @param {Boolean} sortable can this column be sorted
 * @param {Number} width size of the column in pixels
 * @returns {Object} valid config for a ResourceTable column with a display for a date value
 */
export const getDateColumnConfig = ({ field, name, sortable, width }) =>
    getBasicColumnConfig({
        display: (date) => date && moment(date).local().format(sheetConstants.DATE_FORMAT),
        field,
        name,
        sortable,
        width,
    })

const RightCell = styled.div`
    text-align: right;
`
const LeftCell = styled.div`
    text-align: left;
`
const CenterCell = styled.div`
    text-align: center;
`

/**
 * A div that's left-, right-, or center-aligned. If align is undefined, pass through.
 * @param {'left' | 'right' | 'center' | undefined} align text alignment
 * @param children {React.ReactNode}
 * @returns {JSX.Element}
 */
const AlignedCell = ({ align, children }) => {
    switch (align) {
        case "left":
            return <LeftCell>{children}</LeftCell>
        case "right":
            return <RightCell>{children}</RightCell>
        case "center":
            return <CenterCell>{children}</CenterCell>
        case undefined:
            return children
        default:
            throw new Error(`Unknown alignment ${align}`)
    }
}

/**
 * Create a config (Object) for a resource table action column that displays dollars.
 * @param {String} field name of the field where we will find the data on the object to display in this column
 * @param {String} name value we want to display at the top of column
 * @param {Boolean} sortable can this column be sorted
 * @param {Number} width size of the column in pixels
 * @returns {Object} valid config for a ResourceTable column with a display for a date value *
 */
export const getDollarsColumnConfig = ({ field, name, sortable, width }) =>
    getBasicColumnConfig({
        display: centsToCurrencyStr,
        field,
        name,
        sortable,
        width,
        align: "right",
    })

/**
 * Get a config (Object) for a resource table action column
 * to see what each config value does check out propTypes in app/static/frontend/sheets/containers/ResourceTableContainer.jsx
 * @param {Function} display optional function for column display logic (either display or getMenuOptions is required)
 * @param {String} field name of the field where we will find the data on the object to display in this column
 * @param {Function} getMenuOptions optional function which takes args from "field"
 * and returns array of menuOptions for DropdownButton component (either display or getMenuOptions is required)
 * @param {Function} shouldHide whether or not we should display anything in the row
 * @returns {Object} valid config for a ResourceTable action column
 */
export const getActionColumnConfig = ({ display, field = "id", getMenuOptions, shouldHide }) => ({
    name: "Actions",
    field,
    display:
        display ||
        ((...args) =>
            shouldHide && shouldHide(...args) ? null : (
                <div className="action-button">
                    <DropdownButton
                        text="Actions"
                        menuOptions={getMenuOptions(...args)}
                        isTiny
                        type="action-idle"
                        dataCy="search-table-actions-dropdown"
                    />
                </div>
            )),
    width: columnWidthConstants.ACTION_COLUMN_WIDTH,
    resourceTableDisableCellLink: true,
})
